/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/restrict-template-expressions */
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../../shared/hooks';
import type {
  Finding,
  KillChainNarrative,
  KillChainPhase,
  KillChainPhaseInfo,
  LogEntry,
  NarrativeEvent,
  PhaseNarrative,
} from '../../data-layer/insightDashboardTypes';
import { buildKillChainNarrative } from '../../data-layer/insightDashboardUtils';

import './KillChainTimeline.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface KillChainTimelineProps {
  findings: Finding[];
  logEntries: LogEntry[];
  killChainTimeline: KillChainPhaseInfo[];
  onFindingClick?: (finding: Finding) => void;
  className?: string;
}

const defaultProps: Partial<KillChainTimelineProps> = {
  onFindingClick: undefined,
  className: undefined,
};

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PHASE_ORDER: KillChainPhase[] = [
  'reconnaissance',
  'weaponization',
  'exploitation',
  'anti_forensics',
];

const PHASE_ACCENTS: Record<KillChainPhase, string> = {
  reconnaissance: '#5C7AEA',
  weaponization: '#9B59B6',
  exploitation: '#F0883E',
  anti_forensics: '#1ABC9C',
};

const SEVERITY_RING: Record<string, { ring: string; bg: string }> = {
  CRITICAL: { ring: '#e54d4d', bg: 'rgba(229, 77, 77, 0.08)' },
  HIGH: { ring: '#f28c40', bg: 'rgba(242, 140, 64, 0.08)' },
  MEDIUM: { ring: '#e9b84a', bg: 'rgba(233, 184, 74, 0.08)' },
  LOW: { ring: '#4bbe6a', bg: 'rgba(75, 190, 106, 0.08)' },
};

const NODE_COL = '110px';
const LINE_COL = '80px';

const KILL_CHAIN_TITLE = 'Kill Chain Analysis';
const DURATION_LABEL = 'Duration :';
const TOTAL_TIME_LABEL = 'Total Time :';
const CRITICAL_LABEL = 'Critical';
const HIGH_LABEL = 'High';
const MEDIUM_LABEL = 'Medium';
const FINDINGS_LABEL = 'Findings :';
const NONE_LABEL = 'None';
const SUMMARY_LABEL = 'Summary :';
const NO_EVENTS_MSG = 'No events in this phase';
const SESSION_PREFIX = 'S-';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);

  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }

  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;

  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}

function buildGridCols(count: number): string {
  if (count <= 0) {
    return NODE_COL;
  }

  const cols: string[] = [];

  for (let i = 0; i < count; i += 1) {
    cols.push(NODE_COL);

    if (i < count - 1) {
      cols.push(LINE_COL);
    }
  }

  return cols.join(' ');
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Horizontal kill-chain phase strip with per-phase events, session links, and finding drill-down.
 */
function KillChainTimeline({
  findings,
  logEntries,
  killChainTimeline,
  onFindingClick,
  className,
}: KillChainTimelineProps) {
  const history = useHistory();
  const l = useInvertedLocalize();
  const [currentIdx, setCurrentIdx] = useState(0);

  const sessionToRequestId = useMemo(() => {
    const map = new Map<string, string>();

    logEntries.forEach((entry) => {
      if (entry.sessionId && entry.requestId && !map.has(entry.sessionId)) {
        map.set(entry.sessionId, entry.requestId);
      }
    });

    return map;
  }, [logEntries]);

  const navigateToSession = useCallback(
    (sessionId: string) => {
      const requestId = sessionToRequestId.get(sessionId);

      if (requestId) {
        history.push(`/activitylog/${requestId}/view`);
      }
    },
    [sessionToRequestId, history]
  );

  const narrative: KillChainNarrative = buildKillChainNarrative(
    findings,
    logEntries,
    killChainTimeline
  );
  const phase: PhaseNarrative = narrative.phases[currentIdx];
  const accent: string = PHASE_ACCENTS[phase.phase];
  const phaseFindings: Finding[] = phase.findings;
  const phaseEvents: NarrativeEvent[] = phase.events;
  const critCount = phaseFindings.filter(
    (f: Finding) => f.severity === 'CRITICAL'
  ).length;
  const highCount = phaseFindings.filter(
    (f: Finding) => f.severity === 'HIGH'
  ).length;
  const medCount = phaseFindings.filter(
    (f: Finding) => f.severity === 'MEDIUM'
  ).length;

  return (
    <Box className={cx('Sod-KillChainTimeline', className)}>
      {/* ── Header ── */}
      <div className='Sod-KillChainTimeline-header'>
        <span className='Sod-KillChainTimeline-title'>{KILL_CHAIN_TITLE}</span>
      </div>

      {/* ── Phase stepper tabs ── */}
      <div className='Sod-KillChain-stepper'>
        {PHASE_ORDER.map((p, idx) => {
          const pNarrative: PhaseNarrative | undefined = narrative.phases[idx];
          const isCurrent = idx === currentIdx;
          const isPast = idx < currentIdx;

          return (
            <React.Fragment key={p}>
              <button
                type='button'
                className={cx('Sod-KillChain-stepper-tab', {
                  'is-current': isCurrent,
                  'is-past': isPast,
                })}
                style={
                  { '--phase-accent': PHASE_ACCENTS[p] } as React.CSSProperties
                }
                onClick={() => setCurrentIdx(idx)}>
                <span className='Sod-KillChain-stepper-label'>
                  {pNarrative.info.label || p}
                </span>
                <span className='Sod-KillChain-stepper-count'>
                  {pNarrative.findings.length || 0}
                </span>
              </button>
              {idx < PHASE_ORDER.length - 1 && (
                <div className='Sod-KillChain-stepper-arrow'>
                  <svg width='24' height='14' viewBox='0 0 24 14'>
                    <path
                      d='M2 7h18'
                      stroke='#6b7280'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                    <path
                      d='M16 2l5 5-5 5'
                      stroke='#6b7280'
                      strokeWidth='2'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Info bar ── */}
      <div className='Sod-KillChain-infoBar'>
        <div className='Sod-KillChain-infoBar-row1'>
          <div className='Sod-KillChain-infoBar-row1Left'>
            <span className='Sod-KillChain-infoBar-label'>
              {DURATION_LABEL}
            </span>
            <span className='Sod-KillChain-infoBar-timeRange'>
              {phase.info.timeRange}
            </span>
            <span className='Sod-KillChain-infoBar-label'>
              {TOTAL_TIME_LABEL}
            </span>
            <span className='Sod-KillChain-infoBar-duration'>
              {phase.durationSeconds > 0
                ? formatDuration(phase.durationSeconds)
                : '--'}
            </span>
          </div>
          <div className='Sod-KillChain-infoBar-sevChips'>
            {critCount > 0 && (
              <span className='Sod-KillChain-sevChip Sod-KillChain-sevChip--critical'>
                {critCount} {CRITICAL_LABEL}
              </span>
            )}
            {highCount > 0 && (
              <span className='Sod-KillChain-sevChip Sod-KillChain-sevChip--high'>
                {highCount} {HIGH_LABEL}
              </span>
            )}
            {medCount > 0 && (
              <span className='Sod-KillChain-sevChip Sod-KillChain-sevChip--medium'>
                {medCount} {MEDIUM_LABEL}
              </span>
            )}
          </div>
        </div>
        <div className='Sod-KillChain-infoBar-row2'>
          <span className='Sod-KillChain-infoBar-label'>{FINDINGS_LABEL}</span>
          {critCount > 0 && (
            <span className='Sod-KillChain-infoBar-sevText Sod-KillChain-infoBar-sevText--critical'>
              {critCount} {CRITICAL_LABEL}
            </span>
          )}
          {highCount > 0 && (
            <span className='Sod-KillChain-infoBar-sevText Sod-KillChain-infoBar-sevText--high'>
              {highCount} {HIGH_LABEL}
            </span>
          )}
          {medCount > 0 && (
            <span className='Sod-KillChain-infoBar-sevText Sod-KillChain-infoBar-sevText--medium'>
              {medCount} {MEDIUM_LABEL}
            </span>
          )}
          {phaseFindings.length === 0 && (
            <span className='Sod-KillChain-infoBar-summary'>{NONE_LABEL}</span>
          )}
        </div>
        {phase.info.summary && (
          <div className='Sod-KillChain-infoBar-row3'>
            <span className='Sod-KillChain-infoBar-label'>{SUMMARY_LABEL}</span>
            <span className='Sod-KillChain-infoBar-summary'>
              {phase.info.summary}
            </span>
          </div>
        )}
      </div>

      {/* ── Horizontal event flow — CSS grid ── */}
      <div className='Sod-KillChain-eventFlow'>
        {phaseEvents.length === 0 ? (
          <div className='Sod-KillChain-eventFlow-empty'>{NO_EVENTS_MSG}</div>
        ) : (
          <div
            className='Sod-KillChain-grid'
            style={{ gridTemplateColumns: buildGridCols(phaseEvents.length) }}>
            {/* Row 1: dots + lines */}
            {phaseEvents.map((event: NarrativeEvent, idx: number) => {
              const sev =
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive lookup; SEVERITY_RING is Record<string, …>
                SEVERITY_RING[event.finding.severity] ?? SEVERITY_RING.MEDIUM;
              const isFirst = event.stepIndex === 0;
              const isLast = idx === phaseEvents.length - 1;

              const dotInner = (
                <div
                  className={cx('Sod-KillChain-dot', {
                    'Sod-KillChain-dot--large': isFirst,
                    'Sod-KillChain-dot--small': !isFirst,
                  })}
                  style={
                    isFirst
                      ? ({
                          borderColor: sev.ring,
                          backgroundColor: sev.bg,
                          '--dot-glow': sev.ring,
                        } as React.CSSProperties)
                      : {
                          borderColor: 'rgb(104, 107, 113)',
                          backgroundColor: 'rgb(216, 219, 226)',
                        }
                  }
                />
              );

              return (
                <React.Fragment
                  key={`r1-${event.finding.id}-${event.stepIndex}`}>
                  {isFirst ? (
                    <button
                      type='button'
                      className={cx('Sod-KillChain-dotCell', {
                        'Sod-KillChain-dotCell--clickable': true,
                      })}
                      aria-label={l('View finding details')}
                      onClick={() => onFindingClick?.(event.finding)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onFindingClick?.(event.finding);
                        }
                      }}>
                      {dotInner}
                    </button>
                  ) : (
                    <div className='Sod-KillChain-dotCell'>{dotInner}</div>
                  )}
                  {!isLast && (
                    <div
                      className='Sod-KillChain-lineCell'
                      style={{ '--line-color': accent } as React.CSSProperties}>
                      <span
                        className='Sod-KillChain-line-seg'
                        style={{ backgroundColor: accent }}
                      />
                      <span
                        className='Sod-KillChain-line-seg'
                        style={{ backgroundColor: accent }}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Row 2: labels */}
            {phaseEvents.map((event: NarrativeEvent, idx: number) => {
              const isFirst = event.stepIndex === 0;
              const isLast = idx === phaseEvents.length - 1;

              return (
                <React.Fragment
                  key={`r2-${event.finding.id}-${event.stepIndex}`}>
                  <div
                    className={cx('Sod-KillChain-labelCell', {
                      'Sod-KillChain-labelCell--clickable': isFirst,
                    })}>
                    {isFirst ? (
                      <button
                        type='button'
                        className='Sod-KillChain-labelCell-findingBtn'
                        aria-label={l('View finding details')}
                        onClick={() => onFindingClick?.(event.finding)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onFindingClick?.(event.finding);
                          }
                        }}>
                        <span className='Sod-KillChain-label-title'>
                          {event.finding.title}
                        </span>
                        <span className='Sod-KillChain-label-detail'>
                          {event.step.detail ?? event.step.action}
                        </span>
                        {event.step.timestamp && (
                          <span className='Sod-KillChain-label-time'>
                            {event.step.timestamp}
                          </span>
                        )}
                      </button>
                    ) : (
                      <>
                        <span className='Sod-KillChain-label-action'>
                          {event.step.action}
                        </span>
                        <span className='Sod-KillChain-label-detail'>
                          {event.step.detail ?? event.step.action}
                        </span>
                        {event.step.timestamp && (
                          <span className='Sod-KillChain-label-time'>
                            {event.step.timestamp}
                          </span>
                        )}
                      </>
                    )}
                    {event.sessionId && (
                      <button
                        type='button'
                        className='Sod-KillChain-label-session'
                        aria-label={l('Open session {sessionId}', {
                          sessionId: event.sessionId,
                        })}
                        onClick={() => navigateToSession(event.sessionId)}>
                        {SESSION_PREFIX}
                        {event.sessionId}
                      </button>
                    )}
                  </div>
                  {!isLast && <div />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </Box>
  );
}

KillChainTimeline.defaultProps = defaultProps;

export default KillChainTimeline;
